import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import { CardPayment } from "@mercadopago/sdk-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { ensureMercadoPago } from "../../services/mercadoPago";
import { AuthContext } from "../../context/Auth/AuthContext";
import moment from "moment";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  addonRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  counter: {
    display: "inline-flex",
    alignItems: "center",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: "0 4px",
  },
  counterValue: {
    minWidth: 28,
    textAlign: "center",
    fontWeight: 600,
  },
  pendingBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#333" : "#f7f7f7",
  },
}));

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

const CHANGE_STATUS_LABEL = {
  pending_payment: "planAddons.changeStatus.pendingPayment",
  applying: "planAddons.changeStatus.applying",
  scheduled: "planAddons.changeStatus.scheduled",
  requires_new_card: "planAddons.changeStatus.requiresNewCard",
};

// Card "Meu plano": composição atual (plano base + add-ons), contratação de
// usuários/conexões adicionais (upgrade com pró-rata) e agendamento de
// remoção (downgrade no próximo ciclo). Preços SEMPRE calculados no backend.
export default function PlanAddonsCard({ onPayInvoice, onChanged, refreshKey }) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [composition, setComposition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [targetUsers, setTargetUsers] = useState(0);
  const [targetConnections, setTargetConnections] = useState(0);
  const [simulation, setSimulation] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);

  const mpReady = ensureMercadoPago();

  const loadComposition = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription/plan-composition");
      setComposition(data);
      setTargetUsers(data.extras.users);
      setTargetConnections(data.extras.connections);
      setSimulation(null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadComposition();
  }, [loadComposition, refreshKey]);

  const dirty =
    composition &&
    (targetUsers !== composition.extras.users ||
      targetConnections !== composition.extras.connections);

  // simulação de preço no backend (debounced)
  useEffect(() => {
    if (!composition || !dirty) {
      setSimulation(null);
      return undefined;
    }
    setSimulating(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/subscription/addons/simulate", {
          extraUsers: targetUsers,
          extraConnections: targetConnections,
        });
        setSimulation(data);
      } catch (err) {
        setSimulation(null);
        toastError(err);
      } finally {
        setSimulating(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [composition, dirty, targetUsers, targetConnections]);

  const pendingChanges = composition?.pendingChanges || [];
  const hasPending = pendingChanges.length > 0;
  const requiresCard = pendingChanges.some(
    (c) => c.status === "requires_new_card"
  );

  const handleConfirm = async () => {
    if (!simulation) return;
    setSubmitting(true);
    try {
      if (simulation.type === "upgrade") {
        const { data } = await api.post("/subscription/addons", {
          extraUsers: targetUsers,
          extraConnections: targetConnections,
        });
        if (data.applied) {
          toast.success(i18n.t("planAddons.toasts.addonsApplied"));
        } else if (data.invoice) {
          toast.info(i18n.t("planAddons.toasts.payProrata"));
          if (onPayInvoice) onPayInvoice(data.invoice);
        }
      } else if (simulation.type === "downgrade") {
        await api.post("/subscription/addons/downgrade", {
          extraUsers: targetUsers,
          extraConnections: targetConnections,
        });
        toast.success(i18n.t("planAddons.toasts.downgradeScheduled"));
      }
      await loadComposition();
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (request) => {
    try {
      await api.delete(`/subscription/addons/requests/${request.id}`);
      toast.success(i18n.t("planAddons.toasts.requestCancelled"));
      await loadComposition();
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    }
  };

  const handleReplaceSubmit = useCallback(
    async (formData) => {
      try {
        await api.post("/subscription/preapproval/replace", {
          cardTokenId: formData.token,
          payerEmail: formData.payer?.email,
        });
        toast.success(i18n.t("planAddons.toasts.subscriptionMigrated"));
        setReplaceOpen(false);
        await loadComposition();
        if (onChanged) onChanged();
      } catch (err) {
        toastError(err);
        // O Brick só reabilita o botão quando a Promise do onSubmit é rejeitada.
        throw err;
      }
    },
    [loadComposition, onChanged]
  );

  const replaceAmount = useMemo(() => {
    const pending = pendingChanges.find(
      (c) => c.status === "requires_new_card" && c.newMonthlyValue
    );
    return Number(
      pending?.newMonthlyValue || composition?.monthlyValue || 0
    );
  }, [pendingChanges, composition]);

  const replaceInitialization = useMemo(
    () => ({ amount: replaceAmount }),
    [replaceAmount]
  );

  if (user?.profile !== "admin") return null;

  if (loading) {
    return (
      <Paper className={classes.card} variant="outlined">
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (!composition) return null;

  const counter = (label, unitPrice, value, setValue, min) => (
    <Box className={classes.addonRow} my={0.5}>
      <Box className={classes.counter}>
        <IconButton
          size="small"
          disabled={value <= min || hasPending}
          onClick={() => setValue(value - 1)}
        >
          <RemoveCircleOutlineIcon fontSize="small" />
        </IconButton>
        <span className={classes.counterValue}>{value}</span>
        <IconButton
          size="small"
          disabled={hasPending}
          onClick={() => setValue(value + 1)}
        >
          <AddCircleOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="body2">
        {label}{" "}
        <Typography component="span" variant="body2" color="textSecondary">
          {i18n.t("planAddons.unitPrice", { value: formatCurrency(unitPrice) })}
        </Typography>
      </Typography>
    </Box>
  );

  // "Adição de 2 usuários e 1 conexão — nova mensalidade R$ … …"
  const describeChange = (change) => {
    const users = change.deltaUsers
      ? i18n.t("planAddons.pending.users", {
          count: Math.abs(change.deltaUsers),
        })
      : "";
    const connections = change.deltaConnections
      ? i18n.t("planAddons.pending.connections", {
          count: Math.abs(change.deltaConnections),
        })
      : "";
    const items =
      users && connections
        ? i18n.t("planAddons.pending.usersAndConnections", {
            users,
            connections,
          })
        : users || connections;
    return i18n.t(
      change.type === "upgrade"
        ? "planAddons.pending.upgrade"
        : "planAddons.pending.downgrade",
      { items }
    );
  };

  return (
    <Paper className={classes.card} variant="outlined">
      <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {i18n.t("planAddons.title")}
        </Typography>
        <Chip
          size="small"
          label={i18n.t("planAddons.planChip", {
            description: composition.description,
            value: formatCurrency(composition.monthlyValue),
          })}
        />
        <Typography variant="body2" color="textSecondary">
          {i18n.t("planAddons.limits", {
            users: composition.limits.users,
            usersInUse: composition.usage.users,
            connections: composition.limits.connections,
            connectionsInUse: composition.usage.connections,
          })}
        </Typography>
      </Box>

      <Box mt={1}>
        {counter(
          i18n.t("planAddons.extraUsers"),
          composition.unitPrices.extraUser,
          targetUsers,
          setTargetUsers,
          0
        )}
        {counter(
          i18n.t("planAddons.extraConnections"),
          composition.unitPrices.extraConnection,
          targetConnections,
          setTargetConnections,
          0
        )}
      </Box>

      {hasPending && (
        <Box className={classes.pendingBox}>
          {pendingChanges.map((change) => (
            <Box
              key={change.id}
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gridGap={8}
              my={0.5}
            >
              <Chip
                size="small"
                color={
                  change.status === "requires_new_card" ? "secondary" : "default"
                }
                label={
                  CHANGE_STATUS_LABEL[change.status]
                    ? i18n.t(CHANGE_STATUS_LABEL[change.status])
                    : change.status
                }
              />
              <Typography variant="body2">
                {describeChange(change)}
                {" — "}
                {i18n.t("planAddons.pending.newMonthlyFee", {
                  value: formatCurrency(change.newMonthlyValue),
                })}
                {change.type === "downgrade" && change.effectiveAt
                  ? ` ${i18n.t("planAddons.pending.effectiveFrom", {
                      date: moment(change.effectiveAt).format("DD/MM/YYYY"),
                    })}`
                  : ""}
                {change.type === "upgrade" && Number(change.prorataValue) > 0
                  ? ` ${i18n.t("planAddons.pending.prorata", {
                      value: formatCurrency(change.prorataValue),
                    })}`
                  : ""}
              </Typography>
              {change.status === "pending_payment" && change.prorataInvoice && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => onPayInvoice && onPayInvoice(change.prorataInvoice)}
                >
                  {i18n.t("planAddons.payProrata")}
                </Button>
              )}
              {change.status === "requires_new_card" && (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={() => setReplaceOpen(true)}
                >
                  {i18n.t("planAddons.replaceCard")}
                </Button>
              )}
              {["pending_payment", "scheduled"].includes(change.status) && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleCancelRequest(change)}
                >
                  {i18n.t("common.cancel")}
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}

      {dirty && !hasPending && (
        <Box
          mt={1}
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gridGap={12}
        >
          {simulating && <CircularProgress size={18} />}
          {simulation && simulation.type === "upgrade" && (
            <>
              <Typography variant="body2">
                {i18n.t("planAddons.simulation.newMonthlyFee")}{" "}
                <strong>{formatCurrency(simulation.target.monthlyValue)}</strong>
                {simulation.prorataValue > 0 ? (
                  <>
                    {" · "}
                    {i18n.t("planAddons.simulation.prorataNow", {
                      count: simulation.cycle.remainingDays,
                    })}{" "}
                    <strong>{formatCurrency(simulation.prorataValue)}</strong>
                  </>
                ) : (
                  ` · ${i18n.t("planAddons.simulation.noChargeNow")}`
                )}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={submitting}
                onClick={handleConfirm}
              >
                {simulation.prorataValue > 0
                  ? i18n.t("planAddons.simulation.hireAndPay", {
                      value: formatCurrency(simulation.prorataValue),
                    })
                  : i18n.t("planAddons.simulation.hireAddons")}
              </Button>
            </>
          )}
          {simulation && simulation.type === "downgrade" && (
            <>
              <Typography variant="body2">
                {i18n.t("planAddons.simulation.newMonthlyNextCycle")}{" "}
                <strong>{formatCurrency(simulation.target.monthlyValue)}</strong>{" "}
                {i18n.t("planAddons.simulation.noRefund")}
              </Typography>
              {(simulation.usageWarning.users ||
                simulation.usageWarning.connections) && (
                <Typography variant="body2" style={{ color: "#ff9800" }}>
                  {i18n.t("planAddons.simulation.usageWarning")}
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                disabled={submitting}
                onClick={handleConfirm}
              >
                {i18n.t("planAddons.simulation.scheduleReduction")}
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="text"
            disabled={submitting}
            onClick={() => {
              setTargetUsers(composition.extras.users);
              setTargetConnections(composition.extras.connections);
            }}
          >
            {i18n.t("planAddons.undo")}
          </Button>
        </Box>
      )}

      <Dialog
        open={replaceOpen}
        onClose={() => setReplaceOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{i18n.t("planAddons.replaceDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {i18n.t("planAddons.replaceDialog.description", {
              value: formatCurrency(replaceAmount),
            })}
          </Typography>
          {mpReady && replaceOpen && (
            <CardPayment
              initialization={replaceInitialization}
              customization={{ paymentMethods: { maxInstallments: 1 } }}
              onSubmit={handleReplaceSubmit}
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
